import type { Map as MaplibreMap } from 'maplibre-gl';

/** How one finger acts on the map: `greedy` moves the map, `cooperative` scrolls the page around it
 *  (two fingers move the map, a tap lends it the one finger — see `MapGestures`). The widget is
 *  `cooperative` unless the host says otherwise: whether a page can scroll around it is the host's
 *  knowledge — a guess from sizes misses content that arrives later and would trap the finger.
 *  `embed.html` opened as a page of its own says `greedy`. */
export type Gestures = 'cooperative' | 'greedy';

export function parseGestures(value: string | null, fallback: Gestures): Gestures {
  return value === 'cooperative' || value === 'greedy' ? value : fallback;
}

/** Keys that only move the focus through the map — Tab, the modifiers pressed on the way to
 *  Shift+Tab, a screen reader's own modifier (Insert, CapsLock) and AltGr — do not work it; every
 *  other key on the map or one of its buttons does. */
const FOCUS_ONLY_KEYS = new Set(['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'Insert', 'CapsLock', 'AltGraph']);

export const isMapUseKey = (key: string): boolean => !FOCUS_ONLY_KEYS.has(key);

/** a tap: the finger lifted within this distance (px) and time (ms) of where and when it came down */
const TAP_SLOP_PX = 10;
const TAP_MAX_MS = 500;
/** the note on the map stays at least this long, and until the map was first moved */
const NOTE_MIN_MS = 8000;

/**
 * Cooperative gestures with a way for a single finger (WCAG 2.5.1, 2.5.7): one finger scrolls the
 * page, two move the map — and a tap on the map lends it the one finger, until the page scrolls or
 * the visitor taps outside the map. A short note on the map says so, and a screen reader hears it.
 * Mouse and pen are left alone: a mouse drags the map in either mode.
 */
export class MapGestures {
  private mode: Gestures = 'cooperative';
  /** the map has the single finger, after a tap */
  private lent = false;
  private tap: { pointerId: number; x: number; y: number; at: number } | null = null;
  private noteTimer: ReturnType<typeof setTimeout> | undefined;
  /** what the note waits for before it fades: its minimum time, and the first move of the map */
  private noteWaits = { time: false, move: false };
  private readonly note: HTMLElement;

  constructor(private readonly map: MaplibreMap) {
    this.note = document.createElement('p');
    this.note.className = 'map-note';
    this.note.setAttribute('role', 'status');
    map.getContainer().appendChild(this.note);
    // on the canvas only: a tap on a pin opens its place, it does not change how the map moves
    const canvas = map.getCanvas();
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointerup', this.onPointerUp);
    // the browser took the touch over (it scrolls the page): no tap
    canvas.addEventListener('pointercancel', () => (this.tap = null));
    map.on('dragstart', () => {
      if (this.lent) this.noteWaitedFor('move');
    });
  }

  set(mode: Gestures): void {
    // asked again on every resize of the widget: only a change counts, or a lent finger would be taken back
    if (mode === this.mode) return;
    this.giveBack();
    this.mode = mode;
    if (mode === 'greedy') this.map.cooperativeGestures.disable();
    else this.map.cooperativeGestures.enable();
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch' || this.mode === 'greedy' || this.lent) return;
    // a second finger makes it a gesture, not a tap
    this.tap = e.isPrimary ? { pointerId: e.pointerId, x: e.clientX, y: e.clientY, at: e.timeStamp } : null;
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    const tap = this.tap;
    this.tap = null;
    if (!tap || e.pointerId !== tap.pointerId) return;
    if (Math.hypot(e.clientX - tap.x, e.clientY - tap.y) <= TAP_SLOP_PX && e.timeStamp - tap.at <= TAP_MAX_MS) this.lend();
  };

  /** One finger moves the map from now on. */
  private lend(): void {
    this.lent = true;
    this.map.cooperativeGestures.disable();
    const doc = this.map.getContainer().ownerDocument;
    // capture: the scroll of the page or of any scrolling box around the widget (scroll does not bubble)
    doc.defaultView?.addEventListener('scroll', this.giveBack, { capture: true, passive: true });
    doc.addEventListener('pointerdown', this.onPointerDownAnywhere, true);
    this.note.textContent = 'Ein Finger verschiebt jetzt die Karte – zum Scrollen daneben tippen.';
    this.note.classList.remove('is-faded');
    this.noteWaits = { time: true, move: true };
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => this.noteWaitedFor('time'), NOTE_MIN_MS);
  }

  /** The note fades once it has been in sight its minimum time and the visitor has moved the map. */
  private noteWaitedFor(what: 'time' | 'move'): void {
    this.noteWaits[what] = false;
    if (!this.noteWaits.time && !this.noteWaits.move) this.note.classList.add('is-faded');
  }

  /** One finger scrolls the page again. */
  readonly giveBack = (): void => {
    if (!this.lent) return;
    this.lent = false;
    this.map.cooperativeGestures.enable();
    const doc = this.map.getContainer().ownerDocument;
    doc.defaultView?.removeEventListener('scroll', this.giveBack, { capture: true });
    doc.removeEventListener('pointerdown', this.onPointerDownAnywhere, true);
    clearTimeout(this.noteTimer);
    this.note.textContent = '';
  };

  private readonly onPointerDownAnywhere = (e: PointerEvent): void => {
    if (!e.composedPath().includes(this.map.getContainer())) this.giveBack();
  };
}
